/**
 * Data-view expansion: a database node (a node whose data.tables is non-empty)
 * is shown in DATA view as a labeled group container plus one card per table.
 * These derived nodes are DISPLAY-ONLY — they never enter map.nodes, and the
 * editor filters their change ids out of the controlled change stream.
 *
 * Derived id scheme (node ids are uuids, so ':' is a safe separator):
 *   group : dbgrp:<dbNodeId>
 *   table : dbtbl:<dbNodeId>:<tableId>
 */

const GROUP_PAD = 16;
const HEADER = 28;
const TABLE_W = 240;
const TABLE_GAP = 14;
const COL_GAP = 24; // horizontal gap between the view column (left) and source column (right)
const GROUP_SPREAD_GAP = 32; // vertical gap for the residual-overlap cleanup after scaling
const MAX_SPREAD_SCALE = 8; // cap on the uniform spread scale (beyond this, cleanup handles residue)
const TABLE_BASE_H = 92; // title + first row (or "no elements") + add-row + padding
const ROW_H = 20; // per data element BEYOND the first (the first replaces the empty line)
const CHILD_GAP = 16; // vertical gap when re-stacking a group's children in data view
const DEFAULT_CHILD_H = 120; // height estimate for a non-database child (flipped app node)

// Derived nodes carry EXPLICIT width/height. In controlled mode React Flow keeps a
// node visibility:hidden until its measured size round-trips through the nodes prop —
// but derived ids are filtered out of the change stream, so they'd never appear.
// Explicit dimensions make React Flow render them immediately (same as the group).
const tableHeight = (table) => {
  const n = table.dataElements ? table.dataElements.length : 0;
  return TABLE_BASE_H + Math.max(0, n - 1) * ROW_H;
};

export const derivedGroupId = (nodeId) => `dbgrp:${nodeId}`;
export const derivedTableId = (nodeId, tableId) => `dbtbl:${nodeId}:${tableId}`;

export const isExpandable = (node) =>
  node && node.kind === 'app' && Array.isArray(node.data.tables) && node.data.tables.length > 0;

/**
 * A top-level plain app node (kind 'app', no parent, no tables) — the SharePoint / Python /
 * Excel-style source nodes. In data view these are draggable and their position is stored
 * display-only on data.details.dvNodePos (see deriveDataViewNodes). Shared source of truth with
 * LineageCanvas.onNodesChange, which routes such a node's drag to that store instead of map.nodes.
 */
export const isTopLevelPlainApp = (node) =>
  !!node && node.kind === 'app' && !node.parentId && !isExpandable(node);

export const isDerivedId = (id) =>
  typeof id === 'string' && (id.startsWith('dbgrp:') || id.startsWith('dbtbl:'));

/** Parse a derived id → {kind, nodeId, tableId?}; null for a normal id. */
export const parseDerivedId = (id) => {
  if (typeof id !== 'string') return null;
  if (id.startsWith('dbgrp:')) return {kind: 'group', nodeId: id.slice(6)};
  if (id.startsWith('dbtbl:')) {
    const rest = id.slice(6);
    const i = rest.indexOf(':');
    return i === -1 ? null : {kind: 'table', nodeId: rest.slice(0, i), tableId: rest.slice(i + 1)};
  }
  return null;
};

/**
 * Reflow the direct children of any real group whose children flip/expand in data view.
 * Data-view cards are far taller than the compact system-view nodes, so system-view spacing
 * would overlap. To keep the data-view layout matching system view, children are clustered
 * into COLUMNS by their x (preserving the left/right arrangement), then each column is
 * re-stacked by data-view height and the parent grown to fit. Returns a new array; nodes
 * passed through from map.nodes are cloned before any mutation (never mutate Redux state).
 * ponytail: one level of nesting is reflowed; deeper nested groups keep their coords.
 */
// Estimate the DATA-view height of a plain app node (it flips to element rows + the
// add-row input). Generous by design: over-allocating leaves a gap (safe), under-
// allocating overlaps the next sibling.
const flippedNodeHeight = (n) =>
  110 + ((n.data && Array.isArray(n.data.dataElements)) ? n.data.dataElements.length : 0) * 30;

const CHILD_COL_THRESHOLD = 150; // x-distance under which children count as the same column

const restackDatabaseGroups = (out, sourceNodes) => {
  // Any group whose children flip/expand in data view must be reflowed — not just groups
  // containing a database node — otherwise the grown children overflow / overlap.
  const parentIds = new Set(
      sourceNodes
          .filter((n) => n.parentId && (isExpandable(n) || n.kind === 'app'))
          .map((n) => n.parentId),
  );
  if (!parentIds.size) return out;

  const isDerived = (c) => isDerivedId(c.id) || c.kind === 'group';
  const childWidth = (c) => (c.kind === 'app' ? TABLE_W : (c.width || TABLE_W));
  const childHeight = (c) => (isDerived(c) ? (c.height || DEFAULT_CHILD_H) : flippedNodeHeight(c));

  const overrides = new Map(); // id -> {position?, width?, height?}
  parentIds.forEach((parentId) => {
    const children = out.filter((n) => n.parentId === parentId);
    // Cluster children into vertical columns by x so the system-view left/right arrangement
    // is PRESERVED, then re-stack each column by data-view height so nothing overlaps.
    const columns = [];
    children.slice().sort((a, b) => a.position.x - b.position.x).forEach((c) => {
      const col = columns.find((k) => Math.abs(k.x - c.position.x) < CHILD_COL_THRESHOLD);
      if (col) col.items.push(c); else columns.push({x: c.position.x, items: [c]});
    });

    let maxRight = 0;
    let maxBottom = 0;
    columns.forEach((col) => {
      let y = HEADER + GROUP_PAD;
      col.items.slice().sort((a, b) => a.position.y - b.position.y).forEach((c) => {
        const w = childWidth(c);
        const h = childHeight(c);
        // Derived nodes need explicit width/height; plain nodes only get repositioned
        // (RF measures their flipped height) — the estimate just paces the stack.
        overrides.set(c.id, isDerived(c) ?
          {position: {x: col.x, y}, width: w, height: h} :
          {position: {x: col.x, y}});
        maxRight = Math.max(maxRight, col.x + w);
        maxBottom = Math.max(maxBottom, y + h);
        y += h + CHILD_GAP;
      });
    });
    overrides.set(parentId, {width: maxRight + GROUP_PAD, height: maxBottom + GROUP_PAD});
  });

  return out.map((n) => {
    const o = overrides.get(n.id);
    return o ? {...n, ...o} : n;
  });
};

/**
 * Expand database nodes into [group, ...tableCards]; pass every other node through.
 *
 * Layout: when a node holds a VIEW slot (a table whose `dvMeta.role === 'view'`), the view
 * card(s) go in a LEFT column and every other table in a RIGHT column (view auto-expansion);
 * a plain multi-table node with no view stays a single left column. Cards are draggable —
 * a stored `dvMeta[tableId].dataPos` overrides the computed column position, and the group
 * grows to contain wherever the user drops a card.
 */
const aabbOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/**
 * Keep the SYSTEM-view arrangement in data view while making room for the taller expanded
 * clusters. The compact system layout has no overlap; the expanded clusters do. Instead of
 * shoving boxes around (which breaks the row/column alignment the user built), scale every
 * top-level node's POSITION uniformly about the layout's top-left anchor — a similarity
 * transform, so every node keeps its exact relative place (same rows, columns, alignment),
 * the gaps just grow until the bigger boxes fit. Overlap shrinks monotonically as the scale
 * grows (positions spread, box sizes fixed), so binary-search the smallest fitting scale.
 *
 * User-pinned clusters (dvGroupPos) keep their EXACT dropped position (not scaled — so a drag
 * sticks and never compounds across re-derives) and act as fixed obstacles. A final
 * collide-only-down cleanup guarantees zero overlap for the residual cases scaling can't fix
 * (a coincident pair; a movable node landing on a pinned cluster).
 *
 * A movable node's shifted coords must not round-trip into map.nodes (which holds the system-view
 * position): real groups + nested nodes are set draggable:false in deriveDataViewNodes, while a
 * top-level plain app node is draggable but its drag is routed to a display-only store (dvNodePos)
 * by LineageCanvas.onNodesChange, so map.nodes stays clean either way.
 */
const spreadTopLevelGroups = (nodes) => {
  const boxAt = (n, pos) => (n.kind === 'group') ?
    {x: pos.x, y: pos.y, w: n.width || TABLE_W, h: n.height || DEFAULT_CHILD_H} :
    {x: pos.x, y: pos.y, w: (n.measured && n.measured.width) || n.width || TABLE_W, h: flippedNodeHeight(n)};
  // Pinned = a user-dragged top-level node held at its data-view position (acts as an obstacle):
  // a derived cluster (dvGroupPos) or a plain app node (dvNodePos). Both carry __userPositioned,
  // which only ever exists on top-level derive output — never on nested nodes or map.nodes.
  const isPinned = (n) => !n.parentId && !!(n.data && n.data.__userPositioned);

  const topLevel = nodes.filter((n) => !n.parentId);
  const movable = topLevel.filter((n) => !isPinned(n));
  if (!movable.length) return nodes;

  const anchorX = Math.min(...movable.map((n) => n.position.x));
  const anchorY = Math.min(...movable.map((n) => n.position.y));
  const scaled = (n, s) => ({x: anchorX + (n.position.x - anchorX) * s, y: anchorY + (n.position.y - anchorY) * s});
  const movableDisjoint = (s) => {
    const boxes = movable.map((n) => boxAt(n, scaled(n, s)));
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) if (aabbOverlap(boxes[i], boxes[j])) return false;
    }
    return true;
  };

  let s = 1;
  if (movable.length > 1 && !movableDisjoint(1)) {
    let lo = 1;
    let hi = MAX_SPREAD_SCALE;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (movableDisjoint(mid)) hi = mid; else lo = mid;
    }
    s = hi;
  }

  const overrides = new Map();
  movable.forEach((n) => overrides.set(n.id, scaled(n, s)));

  // Cleanup: guarantee zero overlap for residuals (coincident pair the scale can't split, or a
  // movable node overlapping a user-pinned cluster). Push straight down — a no-op when scaling
  // already separated everything and there are no pins.
  const placed = topLevel.filter(isPinned).map((n) => boxAt(n, n.position));
  movable.slice().sort((a, b) => overrides.get(a.id).y - overrides.get(b.id).y).forEach((n) => {
    const g = boxAt(n, overrides.get(n.id));
    for (let guard = 0; guard < 500; guard++) {
      const coll = placed.filter((b) => aabbOverlap(b, g));
      if (!coll.length) break;
      g.y = Math.max(...coll.map((b) => b.y + b.h)) + GROUP_SPREAD_GAP;
    }
    placed.push(g);
    overrides.set(n.id, {x: g.x, y: g.y});
  });

  return nodes.map((n) => (overrides.has(n.id) ? {...n, position: overrides.get(n.id)} : n));
};

export const deriveDataViewNodes = (nodes, mappings = []) => {
  const out = [];
  nodes.forEach((node) => {
    if (!isExpandable(node)) {
      if (isTopLevelPlainApp(node)) {
        // A top-level plain app node IS draggable in data view (issue #647). Its position is
        // stored display-only on data.details.dvNodePos — like a cluster's dvGroupPos / a card's
        // dataPos — so the drop sticks without writing the display coords back into map.nodes
        // (which holds the system-view position). A stored dvNodePos pins it + marks it
        // __userPositioned so spreadTopLevelGroups leaves it put and treats it as an obstacle.
        const dvNodePos = node.data.details && node.data.details.dvNodePos;
        out.push(dvNodePos ?
          {...node, position: dvNodePos, data: {...node.data, __userPositioned: true}} :
          node);
      } else {
        // Real group containers + every nested node are display-repositioned (spread / restack
        // owns their layout), so draggable:false stops applyNodeChanges from writing the shifted
        // coords back into map.nodes and corrupting the system-view position. Reposition these in
        // system view.
        out.push({...node, draggable: false});
      }
      return;
    }
    const gid = derivedGroupId(node.id);
    const tables = node.data.tables;
    const dvMeta = (node.data.details && node.data.details.dvMeta) || {};
    // View/source split for the left/right layout. PRIMARY signal = the intra-node mappings
    // themselves (a table that is a mapping TARGET is the view; a mapping SOURCE is a source
    // table) — always present when a view was expanded, and robust to a missing/legacy dvMeta.
    // dvMeta.role is the fallback (tables with no mappings yet).
    const intra = mappings.filter((m) => m.sourceNodeId === node.id && m.targetNodeId === node.id);
    const viewSlots = new Set(intra.map((m) => m.targetTableId).filter(Boolean));
    const srcSlots = new Set(intra.map((m) => m.sourceTableId).filter(Boolean));
    const roleOf = (t) => viewSlots.has(t.id) ? 'view' :
      (srcSlots.has(t.id) ? 'source' : ((dvMeta[t.id] && dvMeta[t.id].role) || null));
    const posOf = (t) => dvMeta[t.id] && dvMeta[t.id].dataPos;
    const hasView = tables.some((t) => roleOf(t) === 'view');
    const RIGHT_X = GROUP_PAD + TABLE_W + COL_GAP;
    const colX = (t) => (hasView && roleOf(t) !== 'view' ? RIGHT_X : GROUP_PAD);

    // Auto-stack floor per column sits below any user-positioned (dataPos) card in that
    // column, so a later re-expand never drops a card on top of a moved one.
    const floor = {};
    tables.forEach((t) => {
      const dp = posOf(t);
      if (!dp) return;
      const x = colX(t);
      floor[x] = Math.max(floor[x] || (HEADER + GROUP_PAD), dp.y + tableHeight(t) + TABLE_GAP);
    });

    const runningY = {};
    const cards = [];
    tables.forEach((t) => {
      const h = tableHeight(t);
      const x = colX(t);
      const dp = posOf(t);
      let position;
      if (dp) {
        position = {x: dp.x, y: dp.y};
      } else {
        const y = runningY[x] != null ? runningY[x] : (floor[x] != null ? floor[x] : HEADER + GROUP_PAD);
        position = {x, y};
        runningY[x] = y + h + TABLE_GAP;
      }
      cards.push({
        id: derivedTableId(node.id, t.id), kind: 'app', type: 'appNode',
        parentId: gid, extent: 'parent',
        position, width: TABLE_W, height: h,
        // Omit `draggable` so cards inherit `nodesDraggable={viewMode==='edit'}` (draggable in
        // edit, locked in preview). An explicit flag would override that gate.
        deletable: false,
        data: {
          label: t.name, appType: node.data.appType, dataElements: t.dataElements || [],
          objectId: t.objectId || null,
          __dbNodeId: node.id, __tableId: t.id, __derivedTable: true,
        },
      });
    });

    // Group grows to contain every card (baseline = one/two columns), so extent:'parent'
    // never clamps a dropped card outside the container.
    const baseRight = (hasView ? RIGHT_X + TABLE_W : GROUP_PAD + TABLE_W);
    const maxRight = cards.reduce((mx, c) => Math.max(mx, c.position.x + TABLE_W), baseRight);
    const maxBottom = cards.reduce((mx, c) => Math.max(mx, c.position.y + c.height), HEADER + GROUP_PAD);
    // A top-level derived group is draggable in data view (inherits nodesDraggable); a stored
    // dvGroupPos (user drag) fixes its position and marks it __userPositioned so the auto-spread
    // leaves it put. A NESTED derived group stays put + non-draggable — restackDatabaseGroups owns it.
    const dvGroupPos = node.data.details && node.data.details.dvGroupPos;
    const userPositioned = !node.parentId && !!dvGroupPos;
    out.push({
      id: gid, kind: 'group', type: 'groupNode',
      // Preserve the database's nesting so its expansion stays inside a real group.
      parentId: node.parentId, extent: node.extent,
      position: node.parentId ? node.position : (dvGroupPos || node.position),
      width: maxRight + GROUP_PAD, height: maxBottom + GROUP_PAD,
      draggable: node.parentId ? false : undefined, selectable: false, deletable: false, connectable: false,
      data: {
        label: node.data.label || node.data.appType || 'Node', appType: node.data.appType,
        __dbGroup: true, __userPositioned: userPositioned,
        __external: !!(node.data.details && node.data.details.isExternal),
      },
    });
    cards.forEach((c) => out.push(c));
  });
  // Derived nodes are filtered out of the controlled change stream, so RF's measured
  // dimensions never round-trip back into the nodes prop and get wiped on every re-sync —
  // which drops any edge anchored to them. Stamp `measured` explicitly so it survives.
  // restack (nested-in-real-group) → spread (top-level de-overlap) → stamp measured.
  const laid = spreadTopLevelGroups(restackDatabaseGroups(out, nodes));
  return laid.map((n) =>
    isDerivedId(n.id) ? {...n, measured: {width: n.width, height: n.height}} : n);
};

/**
 * The display node id a real node maps to for a NODE-level (system) edge in data view.
 * An expandable node maps to its derived container — so a single node-to-node connection
 * anchors to the parent, not fanned out to every child card (element-level mappings still
 * target individual cards). Returns an array for the caller's uniform handling.
 */
export const resolveDisplayTargets = (node) =>
  isExpandable(node) ? [derivedGroupId(node.id)] : [node.id];
